import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input, output, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { PipesModule } from '@shared/pipes/pipes.module';
import { FieldDefPipe } from '@shared/pipes/field-def.pipe';
import { RelatedToType } from '@shared/enums';
import { environment } from '@env/environment';
import { Dictionary, FieldStylingDirective, TsButtonModule } from 'tsui';
import { Store } from '@ngxs/store';
import { TsSelectModule } from 'tsui/lib/ts-select/ts.select.module';
import { ObjectsManagerState } from '@modules/settings/objects-manager/state/state';
import { Observable } from 'rxjs';
import { FieldDef } from '@core/models';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatLegacySelectChange, MatLegacySelectModule } from '@angular/material/legacy-select';
import { MatLegacyTooltipModule } from '@angular/material/legacy-tooltip';
import { cloneDeep } from 'lodash';

type Summary = { titleField: string; addressField: string; fields: string[] };
type Styling = { color: string; is_bold: boolean };
const summaryDefaultValue = { titleField: 'name', addressField: 'address_string', fields: ['id'] };

@Component({
  selector: 'dashboard-summary',
  standalone: true,
  imports: [
    NgTemplateOutlet,
    MatIconModule,
    PipesModule,
    FieldDefPipe,
    FieldStylingDirective,
    TsButtonModule,
    TsSelectModule,
    AsyncPipe,
    ReactiveFormsModule,
    MatLegacyTooltipModule,
    MatLegacySelectModule,
  ],
  templateUrl: './dashboard-summary.component.html',
  styleUrl: './dashboard-summary.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardSummaryComponent {
  store = inject(Store);
  formBuilder = inject(FormBuilder);
  summaryChanged = output<any>();
  options = input.required();
  editMode = input.required({
    transform: (editMode: boolean) => {
      return editMode;
    },
  });
  relatedTo = input.required({
    transform: (relatedTo: RelatedToType) => {
      if (relatedTo) {
        this.fields$ = this.store.select(ObjectsManagerState.getFieldsByObjectName(relatedTo as string));
      }

      return relatedTo;
    },
  });
  entity = input.required({
    transform: (model: any) => {
      if (!model) {
        return model;
      }

      if (model?.address) {
        const { latitude, longitude } = model.address;
        this.mapImgUrl.set(
          `https://maps.googleapis.com/maps/api/staticmap?zoom=12&size=175x175&maptype=roadmap%20&markers=color:red%7C${latitude},${longitude}%20&key=${environment.google.streetViewKey}`,
        );
      } else {
        this.mapImgUrl.set(undefined);
      }

      if (model?.template?.definition?.dashboard?.summary) {
        this.summary.set(model.template.definition.dashboard.summary);
      } else {
        this.summary.set(summaryDefaultValue);
      }

      this.fieldStylingMap.set(
        this.summary().fields.reduce((p, c) => {
          p[c] = {
            color: model.meta?.[c]?.conditional_styling?.color,
            is_bold: model.meta?.[c]?.conditional_styling?.is_bold,
          };
          return p;
        }, {}),
      );

      this.summaryChanged.emit(this.summary());

      return cloneDeep(model);
    },
  });
  mapImgUrl = signal<string>(undefined);
  RelatedToType = RelatedToType;
  fields$: Observable<FieldDef[]>;
  form = new FormGroup({
    fields: this.formBuilder.array([]),
  });
  summary = signal<Summary>(summaryDefaultValue);
  fieldStylingMap = signal<Dictionary<Styling>>({});

  addField(): void {
    this.summary.update((current) => {
      return { ...current, fields: [...current.fields, 'id'] };
    });

    this.summaryChanged.emit(this.summary());
  }

  removeField(index: number): void {
    this.summary.update((current) => {
      return { ...current, fields: current.fields.filter((_, i) => i !== index) };
    });

    this.summaryChanged.emit(this.summary());
  }

  fieldChanged(event: MatLegacySelectChange, index: number): void {
    this.summary.update((current) => {
      const fields = [...current.fields];
      fields[index] = event.value;
      return { ...current, fields };
    });

    this.summaryChanged.emit(this.summary());
  }
}
